import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Button,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import {useNavigate, useSearchParams} from 'react-router';
import {useSession} from '../../firebase/UserProvider';
import {fetchRequirementsConfig} from '../../firebase/firestore/icr';
import {
    buildRequirementsListSearch,
    DEFAULT_GAP_STATUS,
    DEFAULT_STATUS,
    parseRequirementsListSearch,
} from './requirementsListUrlState';
import RequirementsCollectionPanel from './RequirementsCollectionPanel';
import RequirementsOverviewPanel from './RequirementsOverviewPanel';

function isCollectionManager(role, roles) {
    return role === 'collection_manager'
        || (Array.isArray(roles) && roles.includes('collection_manager'));
}

function sessionDefaultOwner(user, role, roles) {
    if (!isCollectionManager(role, roles)) return null;
    return user?.uid || user?.id || user?.displayName || null;
}

function entityLabel(tab, count) {
    if (tab === 'eei') return count == null ? 'EEIs' : `${count} EEIs`;
    if (tab === 'gap') return count == null ? 'Gaps' : `${count} Gaps`;
    return count == null ? 'ICRs' : `${count} ICRs`;
}

/**
 * Requirements page shell: flat Overview | ICR | EEI | Gaps tabs.
 * Parent owns tab + shared Owner/Tags; Overview hides table search/filters.
 * Overview tab visibility is gated by feature flag `requirements.overview-enabled`.
 */
const ListOfIntCollRequests = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {user, role, roles} = useSession();
    const canCreate = role === 'collection_manager' || role === 'reviewer'
        || role === 'admin' || (roles || []).includes('admin');

    const initialListState = useMemo(
        () => parseRequirementsListSearch(searchParams),
        // Intentionally only hydrate from the URL on first mount (Back restores via returnTo).
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const [overviewEnabled, setOverviewEnabled] = useState(null); // null until config loads
    const [tab, setTab] = useState(initialListState.tab);
    const [searchQ, setSearchQ] = useState(initialListState.q);
    const [filtersOpen, setFiltersOpen] = useState(initialListState.filtersOpen);
    const [filterTags, setFilterTags] = useState(initialListState.tags);
    // URL owners are user-chosen. The CM session default is not — Gaps filter
    // reports.owner_user_id (FINTEL author), so applying that default hides every gap.
    const [ownersUserChosen, setOwnersUserChosen] = useState(
        () => initialListState.owners.length > 0,
    );
    const [filterOwners, setFilterOwners] = useState(() => {
        if (initialListState.owners.length > 0) return initialListState.owners;
        if (initialListState.tab === 'gap') return [];
        const defaultOwner = sessionDefaultOwner(user, role, roles);
        if (defaultOwner) return [defaultOwner];
        return [];
    });
    const [filterPriority, setFilterPriority] = useState(initialListState.priority);
    const [filterClassification, setFilterClassification] = useState(initialListState.classification);
    const [filterStatus, setFilterStatus] = useState(initialListState.status);
    // Default matches server sort (overlap FULL/PARTIAL desc); show the indicator immediately.
    const [sort, setSort] = useState({by: initialListState.sortBy, dir: initialListState.sortDir});
    const sortBy = sort.by;
    const sortDir = sort.dir;
    const [listTotal, setListTotal] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetchRequirementsConfig()
            .then((cfg) => {
                if (cancelled) return;
                const enabled = cfg?.overviewEnabled !== false;
                setOverviewEnabled(enabled);
                if (!enabled) {
                    setTab((current) => (current === 'overview' ? 'icr' : current));
                }
            })
            .catch(() => {
                if (!cancelled) setOverviewEnabled(true);
            });
        return () => { cancelled = true; };
    }, []);

    // Keep /icr query string in sync so detail Back (returnTo) restores filters/sort.
    useEffect(() => {
        const next = buildRequirementsListSearch({
            tab,
            q: searchQ,
            tags: filterTags,
            owners: filterOwners,
            priority: filterPriority,
            classification: filterClassification,
            status: filterStatus,
            sortBy,
            sortDir,
            filtersOpen,
        });
        if (next.toString() === searchParams.toString()) return;
        setSearchParams(next, {replace: true});
    }, [
        tab, searchQ, filterTags, filterOwners, filterPriority, filterClassification,
        filterStatus, sortBy, sortDir, filtersOpen, searchParams, setSearchParams,
    ]);

    const handleTotalChange = useCallback((total) => {
        setListTotal(total);
    }, []);

    const showOverviewTab = overviewEnabled === true;
    // While config is loading, keep Overview selection pending so we don't flash the ICR table.
    const effectiveTab = overviewEnabled === null && tab === 'overview'
        ? null
        : (showOverviewTab ? tab : (tab === 'overview' ? 'icr' : tab));

    const subtitle = useMemo(() => {
        if (effectiveTab == null || effectiveTab === 'overview') {
            return effectiveTab === 'overview' ? 'Coverage overview' : 'Requirements';
        }
        return entityLabel(effectiveTab, listTotal);
    }, [effectiveTab, listTotal]);

    const handleOwnersChange = useCallback((next) => {
        setOwnersUserChosen(true);
        setFilterOwners(Array.isArray(next) ? next : []);
    }, []);

    const applyTabChange = useCallback((nextTab) => {
        if (nextTab === 'gap' && tab !== 'gap') {
            setFilterStatus([...DEFAULT_GAP_STATUS]);
            if (!ownersUserChosen) {
                setFilterOwners([]);
            }
        } else if (tab === 'gap' && nextTab !== 'gap') {
            if (nextTab === 'icr' || nextTab === 'eei') {
                setFilterStatus([...DEFAULT_STATUS]);
            }
            if (!ownersUserChosen) {
                const defaultOwner = sessionDefaultOwner(user, role, roles);
                setFilterOwners(defaultOwner ? [defaultOwner] : []);
            }
        }
        setTab(nextTab);
    }, [tab, ownersUserChosen, user, role, roles]);

    const handleTabChange = useCallback((_, nextTab) => {
        applyTabChange(nextTab);
    }, [applyTabChange]);

    return (
        <Stack p={2} spacing={2.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                <Stack spacing={0.5}>
                    <Typography variant="h5" fontWeight={600} letterSpacing={-0.3}>
                        Requirements
                    </Typography>
                    <Typography variant="caption" color="text.secondary" letterSpacing={0.3}>
                        {subtitle}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} flexShrink={0} alignItems="center">
                    {canCreate && (
                        <Tooltip title="Intelligence Collection Requirement" arrow placement="bottom">
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => navigate('/icr/new')}
                            >
                                New ICR
                            </Button>
                        </Tooltip>
                    )}
                </Stack>
            </Stack>

            <Tabs
                value={effectiveTab ?? false}
                onChange={handleTabChange}
                sx={{minHeight: 36, borderBottom: 1, borderColor: 'divider'}}
            >
                {showOverviewTab && (
                    <Tab
                        value="overview"
                        label="Overview"
                        sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                    />
                )}
                <Tab
                    value="icr"
                    aria-label="Intelligence Collection Requirement"
                    label={
                        <Tooltip title="Intelligence Collection Requirement" arrow placement="bottom" describeChild>
                            <span>ICR</span>
                        </Tooltip>
                    }
                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                />
                <Tab
                    value="eei"
                    aria-label="Essential Elements of Information"
                    label={
                        <Tooltip title="Essential Elements of Information" arrow placement="bottom" describeChild>
                            <span>EEI</span>
                        </Tooltip>
                    }
                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                />
                <Tab
                    value="gap"
                    label="Gaps"
                    sx={{minHeight: 36, textTransform: 'none', fontWeight: 600}}
                />
            </Tabs>

            {effectiveTab == null ? null : effectiveTab === 'overview' ? (
                <RequirementsOverviewPanel
                    owners={filterOwners}
                    tags={filterTags}
                    onOwnersChange={handleOwnersChange}
                    onTagsChange={setFilterTags}
                    onSwitchTab={applyTabChange}
                />
            ) : (
                <RequirementsCollectionPanel
                    entityType={effectiveTab}
                    searchQ={searchQ}
                    onSearchQChange={setSearchQ}
                    filterTags={filterTags}
                    onFilterTagsChange={setFilterTags}
                    filterOwners={filterOwners}
                    onFilterOwnersChange={handleOwnersChange}
                    filterPriority={filterPriority}
                    onFilterPriorityChange={setFilterPriority}
                    filterClassification={filterClassification}
                    onFilterClassificationChange={setFilterClassification}
                    filterStatus={filterStatus}
                    onFilterStatusChange={setFilterStatus}
                    filtersOpen={filtersOpen}
                    onFiltersOpenChange={setFiltersOpen}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSortChange={setSort}
                    onTotalChange={handleTotalChange}
                />
            )}
        </Stack>
    );
};

export default ListOfIntCollRequests;
